import { create } from "zustand";
import { persist } from "zustand/middleware";
import { api, ApiError, newKey } from "@/lib/api";
import { queryClient } from "@/lib/queryClient";
import { queryKeys } from "@/lib/queryKeys";
import { toast } from "./feedback";
import { useConnection } from "./connection";

/*
 * Upkeep's outbox, the booking outbox's twin: submits, assignments, finding
 * actions and laundry batches wait here when there is no signal. Each carries
 * a key minted on the phone, so however often it is retried the server does
 * it once. Nothing here can send a lockbox code: the server decides that
 * when the submit lands.
 */

export type UpkeepJob = {
  key: string;
  path: string;
  body: unknown;
  /** "Checkout check · B6", for the toast and the pill. */
  label: string;
  /** What it answered, once it did: the submit's result, the new batch. */
  status: "queued" | "sending" | "failed";
  error?: string;
  createdAt: number;
};

export type JobResult<T = unknown> = { kind: "done"; data: T } | { kind: "queued" } | { kind: "failed"; error: ApiError };

type State = {
  jobs: UpkeepJob[];
  /** Queue a write and try it straight away. */
  run: <T>(job: { path: string; body: unknown; label: string; key?: string }) => Promise<JobResult<T>>;
  flush: () => Promise<void>;
  discard: (key: string) => void;
};

let flushing = false;

async function attempt<T>(job: UpkeepJob): Promise<JobResult<T>> {
  try {
    const data = await api<T>(job.path, { method: "POST", body: job.body, headers: { "Idempotency-Key": job.key } });
    void queryClient.invalidateQueries({ queryKey: queryKeys.upkeep.all });
    return { kind: "done", data };
  } catch (e) {
    const err = e instanceof ApiError ? e : new ApiError(500, "internal", String(e));
    if (err.isNetwork || err.status === 401) return { kind: "queued" };
    return { kind: "failed", error: err };
  }
}

export const useUpkeepOutbox = create<State>()(
  persist(
    (set, get) => ({
      jobs: [],
      run: async <T,>({ path, body, label, key }: { path: string; body: unknown; label: string; key?: string }) => {
        const job: UpkeepJob = { key: key ?? newKey(), path, body, label, status: "sending", createdAt: Date.now() };
        set((s) => ({ jobs: [...s.jobs.filter((j) => j.key !== job.key), job] }));
        const r = await attempt<T>(job);
        set((s) => ({
          jobs:
            r.kind === "done"
              ? s.jobs.filter((j) => j.key !== job.key)
              : s.jobs.map((j) => (j.key === job.key ? { ...j, status: r.kind === "queued" ? "queued" : "failed", error: r.kind === "failed" ? r.error.message : undefined } : j)),
        }));
        return r;
      },
      flush: async () => {
        if (flushing) return;
        flushing = true;
        try {
          for (const job of get().jobs.filter((j) => j.status === "queued")) {
            set((s) => ({ jobs: s.jobs.map((j) => (j.key === job.key ? { ...j, status: "sending" } : j)) }));
            const r = await attempt(job);
            if (r.kind === "queued") {
              set((s) => ({ jobs: s.jobs.map((j) => (j.key === job.key ? { ...j, status: "queued" } : j)) }));
              break; // still offline: try again later
            }
            if (r.kind === "done") {
              set((s) => ({ jobs: s.jobs.filter((j) => j.key !== job.key) }));
              toast.saved(`${job.label} synced`);
            } else {
              set((s) => ({ jobs: s.jobs.map((j) => (j.key === job.key ? { ...j, status: "failed", error: r.error.message } : j)) }));
              toast.error(`${job.label}: ${r.error.message}`);
            }
          }
        } finally {
          flushing = false;
        }
      },
      discard: (key) => set((s) => ({ jobs: s.jobs.filter((j) => j.key !== key) })),
    }),
    {
      name: "kk-upkeep-outbox",
      merge: (persisted, current) => ({
        ...current,
        jobs: ((persisted as { jobs?: UpkeepJob[] })?.jobs ?? []).map((j) => (j.status === "sending" ? { ...j, status: "queued" } : j)),
      }),
    },
  ),
);

if (typeof window !== "undefined") {
  const tryFlush = () => {
    if (useUpkeepOutbox.getState().jobs.some((j) => j.status === "queued")) void useUpkeepOutbox.getState().flush();
  };
  window.addEventListener("online", tryFlush);
  useConnection.subscribe((s, prev) => {
    if (s.online && !prev.online) tryFlush();
  });
  setInterval(tryFlush, 20_000);
}
